import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatCustomValue,
  type CustomFieldAnswer,
  type CustomFieldTarget,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { field } from '../create/Field';

/**
 * The shop's own questions on one record (PRD v2 §2.2) — the same card on customers,
 * properties, jobs, quotes, invoices and the tech's phone.
 *
 * Edits save on BLUR, not behind a Save button. These are one-line answers a person fills
 * in while doing something else, and a form with a save button collects half-typed values
 * that never get committed. A checkbox or a dropdown saves the moment it changes.
 *
 * A shop with no custom fields sees NOTHING here — the card removes itself rather than
 * occupying a slot on every detail page to say "none".
 */
export function CustomFieldsCard({
  entityType,
  entityId,
  title = 'Details',
  /** The phone gets only the fields the office marked field-relevant. */
  techOnly = false,
}: {
  entityType: CustomFieldTarget;
  entityId: string;
  title?: string;
  techOnly?: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery(
    trpc.customField.answers.queryOptions({ entityType, entityId, techOnly }),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation(
    trpc.customField.set.mutationOptions({
      onSuccess: () => {
        setError(null);
        void qc.invalidateQueries();
      },
      // The server writes these for a person ("Units takes a number"); show what it said.
      onError: (e) => setError(e.message),
    }),
  );

  const answers = (q.data ?? []) as CustomFieldAnswer[];

  // Nothing defined, nothing to show. Not an empty state — an absent card.
  if (q.isPending || answers.length === 0) return null;

  return (
    <SectionCard title={title}>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <dl className="space-y-3">
        {answers.map((a) => (
          <AnswerRow
            key={a.definitionId}
            answer={a}
            saving={save.isPending}
            onSave={(value) =>
              save.mutate({
                definitionId: a.definitionId,
                entityId,
                value,
                ...idem(),
              })
            }
          />
        ))}
      </dl>
    </SectionCard>
  );
}

function AnswerRow({
  answer,
  saving,
  onSave,
}: {
  answer: CustomFieldAnswer;
  saving: boolean;
  onSave: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(answer.value ?? '');

  // A refetch after somebody else's edit has to win over a stale local draft, but only
  // while this field isn't the one being typed in.
  useEffect(() => {
    setDraft(answer.value ?? '');
  }, [answer.value]);

  const commit = () => {
    const next = draft.trim();
    if (next === (answer.value ?? '')) return; // nothing changed; don't write a row
    onSave(next === '' ? null : next);
  };

  const label = (
    <dt className="text-xs font-medium text-muted-foreground">
      {answer.label}
      {answer.required ? <span className="text-[var(--sw-danger-text)]"> *</span> : null}
    </dt>
  );

  if (answer.kind === 'checkbox') {
    return (
      <div>
        <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4"
            checked={answer.value === 'true'}
            disabled={saving}
            onChange={(e) => onSave(String(e.target.checked))}
          />
          {answer.label}
        </label>
        {answer.helpText ? (
          <p className="text-xs text-muted-foreground">{answer.helpText}</p>
        ) : null}
      </div>
    );
  }

  if (answer.kind === 'select') {
    return (
      <div>
        {label}
        <dd>
          <select
            className={field}
            aria-label={answer.label}
            value={answer.value ?? ''}
            disabled={saving}
            onChange={(e) => onSave(e.target.value || null)}
          >
            {/* Blank stays available unless the field is required — an answer nobody has
                given yet must not be forced to become the first option by accident. */}
            {answer.required && answer.value ? null : (
              <option value="">{answer.required ? 'Pick one' : 'Not answered'}</option>
            )}
            {(answer.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </dd>
        {answer.helpText ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{answer.helpText}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      {label}
      <dd>
        <input
          className={field}
          aria-label={answer.label}
          type={answer.kind === 'date' ? 'date' : 'text'}
          inputMode={answer.kind === 'number' ? 'decimal' : undefined}
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            // Escape abandons the edit rather than committing a half-typed value.
            if (e.key === 'Escape') {
              setDraft(answer.value ?? '');
              e.currentTarget.blur();
            }
          }}
          placeholder={answer.value === null ? '—' : undefined}
        />
      </dd>
      {answer.helpText ? (
        <p className="mt-0.5 text-xs text-muted-foreground">{answer.helpText}</p>
      ) : null}
    </div>
  );
}

/** Read-only rendering for somewhere a form would be wrong — a PDF, a summary strip. */
export function CustomFieldsList({ answers }: { answers: CustomFieldAnswer[] }) {
  if (answers.length === 0) return null;
  return (
    <dl className="space-y-1 text-sm">
      {answers.map((a) => (
        <div key={a.definitionId} className="flex justify-between gap-3">
          <dt className="text-muted-foreground">{a.label}</dt>
          <dd>{formatCustomValue(a)}</dd>
        </div>
      ))}
    </dl>
  );
}
